import { useEffect, useState } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Sparkles, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocation } from "@/components/location-provider";

interface AuroraResponse {
  forecast: { ts: number; kp: number; observed: string }[];
  currentKp: number | null;
  peakKp: number;
  peakAt: number | null;
  viewerLat: number;
  viewerVisible: boolean;
  viewerHint: string;
}

function kpColor(kp: number): { text: string; bg: string; bar: string; ring: string } {
  if (kp >= 7) return { text: "text-rose-400", bg: "bg-rose-500/15", bar: "bg-rose-400", ring: "ring-rose-500/30" };
  if (kp >= 5) return { text: "text-orange-400", bg: "bg-orange-500/15", bar: "bg-orange-400", ring: "ring-orange-500/30" };
  if (kp >= 4) return { text: "text-amber-400", bg: "bg-amber-500/15", bar: "bg-amber-400", ring: "ring-amber-500/30" };
  if (kp >= 3) return { text: "text-lime-400", bg: "bg-lime-500/15", bar: "bg-lime-400", ring: "ring-lime-500/30" };
  return { text: "text-sky-400", bg: "bg-sky-500/15", bar: "bg-sky-400", ring: "ring-sky-500/30" };
}

function fmtTime(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function fmtDay(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleDateString([], { weekday: "short" });
}

export function AuroraPanel() {
  const [data, setData] = useState<AuroraResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { current: location } = useLocation();

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`/api/aurora?lat=${location.lat}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [location.lat]);

  if (loading) {
    return (
      <GlassPanel intensity="low" className="p-4">
        <div className="h-32 animate-pulse rounded-xl bg-muted/30" />
      </GlassPanel>
    );
  }

  if (error || !data) {
    return null;
  }

  const peakColor = kpColor(data.peakKp);
  const currentColor = data.currentKp != null ? kpColor(data.currentKp) : peakColor;

  // For the bar chart, show the next ~24 hours (8 entries at 3h spacing).
  const chart = data.forecast.slice(0, 8);
  const max = Math.max(9, ...chart.map((c) => c.kp));

  return (
    <GlassPanel intensity="low" className="p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10 ring-1 ring-violet-500/20">
            <Sparkles className="h-4 w-4 text-violet-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight">Aurora Forecast</h3>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              KP index · NOAA SWPC
            </p>
          </div>
        </div>
        <div
          className={cn(
            "rounded-xl px-3 py-2 text-right ring-1 flex-shrink-0",
            peakColor.bg,
            peakColor.ring,
          )}
        >
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
            Peak KP
          </p>
          <p className={cn("text-xl font-bold tabular-nums", peakColor.text)}>
            {data.peakKp.toFixed(1)}
          </p>
        </div>
      </div>

      {/* Visibility + current chip row */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg bg-background/40 border border-border/40 px-3 py-2">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
            Now
          </p>
          <p className={cn("text-sm font-bold tabular-nums", currentColor.text)}>
            KP {data.currentKp != null ? data.currentKp.toFixed(1) : "–"}
          </p>
        </div>
        <div
          className={cn(
            "rounded-lg border px-3 py-2",
            data.viewerVisible
              ? "bg-emerald-500/10 border-emerald-500/30"
              : "bg-muted/20 border-border/40",
          )}
        >
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
            {data.viewerVisible ? (
              <Eye className="h-2.5 w-2.5 text-emerald-400" />
            ) : (
              <EyeOff className="h-2.5 w-2.5 text-muted-foreground" />
            )}
            From your latitude
          </p>
          <p
            className={cn(
              "text-xs font-bold leading-tight mt-0.5",
              data.viewerVisible ? "text-emerald-300" : "text-foreground/80",
            )}
          >
            {data.viewerVisible ? "Possibly visible" : "Below the auroral oval"}
          </p>
        </div>
      </div>

      {/* Bar chart of next 24h KP */}
      <div className="rounded-lg bg-background/30 border border-border/40 p-2.5">
        <div className="flex items-end gap-1 h-16">
          {chart.map((c) => {
            const cc = kpColor(c.kp);
            const h = Math.max(6, (c.kp / max) * 100);
            return (
              <div
                key={c.ts}
                className="flex-1 flex flex-col items-center gap-1 min-w-0"
                title={`${fmtDay(c.ts)} ${fmtTime(c.ts)} · KP ${c.kp.toFixed(1)} (${c.observed})`}
              >
                <div className="w-full flex-1 flex items-end">
                  <div
                    className={cn("w-full rounded-t-sm transition-all", cc.bar)}
                    style={{ height: `${h}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[9px] text-muted-foreground tabular-nums">
          <span>{chart[0] ? fmtTime(chart[0].ts) : ""}</span>
          <span>next 24h</span>
          <span>{chart[chart.length - 1] ? fmtTime(chart[chart.length - 1].ts) : ""}</span>
        </div>
      </div>

      <p className="mt-3 text-[11px] text-muted-foreground">
        {data.viewerHint}
        {data.peakAt != null && (
          <span className="text-foreground/70">
            {" "}Peak around {fmtTime(data.peakAt)} ({fmtDay(data.peakAt)}).
          </span>
        )}
      </p>
    </GlassPanel>
  );
}
